/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { Checkbox, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { allowMissingDocument } from "../../../services/inquiryToCustomerService";
import FormControlLabel from "@mui/material/FormControlLabel";
import { fileCheck, formValue, saveMediaObject } from "../../../services/formServiceFunctions";
import FileUploadForm from "../../Form/Common/FileUploadSection/FileUploadForm";
import { MediaObject } from "../../../types/Entities/MediaObject/MediaObject";
import React, { useState } from "react";
import InquiryToCustomer, { RequestedDocument } from "../../../types/Entities/InquiryToCustomer";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createMediaObject } from "../../../services/reactQuery/reactQueryMediaObjectService";
import { queryKeys, updateResource } from "../../../services/reactQuery/reactQueryService";
import { useForm } from "../../../provider/Form/FormProvider";
import structuredClone from "@ungap/structured-clone";
import * as messagesActions from "../../../store/messages/actions";
import _head from "lodash/head";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import dayjs from "dayjs";

type InquiryToCustomerDocumentProps = {
  document: RequestedDocument;
  inquiryToCustomer: InquiryToCustomer;
  index: number;
  product: AbstractCase;
  onHandleUploading: (isUploading: boolean) => void;
  isBlocked?: boolean;
};

export default function InquiryToCustomerDocument({
  document,
  inquiryToCustomer,
  product,
  index,
  onHandleUploading,
  isBlocked,
}: InquiryToCustomerDocumentProps) {
  const [filePath, setFilePath] = useState("");
  const createMediaObjectMutation = useMutation({
    mutationFn: createMediaObject,
  });
  const queryClient = useQueryClient();
  const [fileUploadKey, setFileUploadKey] = useState(dayjs().toISOString());

  const inquiryApiUri = "/inquiry_to_customers";

  const updateMutation = useMutation({
    mutationFn: updateResource,
    onSuccess: (data) => queryClient.setQueryData(queryKeys.item(inquiryApiUri, inquiryToCustomer.id), data),
  });
  const { handleChange, values } = useForm();

  const updateInquiryToCustomerMediaObjects = async (requestedDocuments: Array<RequestedDocument>) => {
    await updateMutation.mutateAsync({
      uri: inquiryApiUri,
      id: inquiryToCustomer.id,
      data: {
        requestedDocuments: requestedDocuments,
      },
    });
  };

  const deleteMediaObject = async (mediaObjectToDelete: MediaObject, index: number) => {
    if (!inquiryToCustomer.requestedDocuments) {
      return;
    }
    onHandleUploading(true);
    let requestedDocumentsClone = structuredClone(inquiryToCustomer.requestedDocuments);
    requestedDocumentsClone[index].temporaryFilesToCombine = requestedDocumentsClone[index].temporaryFilesToCombine.filter(
      (mediaObject) => mediaObject.id !== mediaObjectToDelete.id
    );
    await updateInquiryToCustomerMediaObjects(requestedDocumentsClone);

    setFileUploadKey(dayjs().toISOString());
    onHandleUploading(false);
  };

  const handleMediaObjectUpload = async (target: HTMLInputElement, index: number) => {
    if (!inquiryToCustomer.requestedDocuments) {
      return;
    }
    onHandleUploading(true);
    const fileCheckRes = fileCheck(target);
    if (fileCheckRes.hasError) {
      messagesActions.addMessage({ type: "error", text: fileCheckRes.errorMsg });
      return;
    }
    const file = _head(target.files)!;
    setFilePath(file.name);

    const mediaObject = await saveMediaObject(createMediaObjectMutation, {
      file: file,
      fieldname: "inquiryTemporaryFile",
      description: "TODO",
      product: product.name,
      productId: product.id,
    });

    if (mediaObject) {
      let requestedDocumentsClone = structuredClone(inquiryToCustomer.requestedDocuments);

      requestedDocumentsClone[index].temporaryFilesToCombine = [...requestedDocumentsClone[index].temporaryFilesToCombine, mediaObject];
      await updateInquiryToCustomerMediaObjects(requestedDocumentsClone);
    }

    setFilePath("");
    onHandleUploading(false);
  };

  return (
    <>
      <Grid container>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Typography variant={"h5"}>{document.documentName}</Typography>
          {allowMissingDocument(document, inquiryToCustomer) && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={formValue(values, "requestedDocumentMissing" + index)}
                  name={"requestedDocumentMissing" + index}
                  onChange={handleChange}
                  disabled={document.temporaryFilesToCombine && document.temporaryFilesToCombine.length > 0}
                />
              }
              label={"Dieses Dokument liegt mir leider nicht vor"}
            />
          )}
        </Grid>
        <Grid
          sx={{ paddingTop: "2rem" }}
          size={{
            xs: 12,
            md: 6
          }}>
          <FileUploadForm
            key={fileUploadKey}
            mediaObjectCollection={document.temporaryFilesToCombine}
            deleteMediaObject={(mediaObjectToDelete: MediaObject) => deleteMediaObject(mediaObjectToDelete, index)}
            handleFileChange={({ target }: any) => handleMediaObjectUpload(target, index)}
            fieldname={"inquiryTemporaryFile" + index}
            allowMore={document.temporaryFilesToCombine.length < 1 || document.temporaryFilesToCombine[0].mimeType !== "application/pdf"}
            filePath={filePath}
            isBlocked={isBlocked || formValue(values, "requestedDocumentMissing" + index)}
            label={""}
            helperText={undefined}
            noSection={undefined}
            headline={undefined}
          />
        </Grid>
      </Grid>
    </>
  );
}
